
/**
 * Search Orchestrator: Advanced Search & Fuzzy Logic initialization
 * Replaces legacy Phase 10 initializer with clearer naming
 */
import { AdvancedSearchEngine } from '../search/advanced-search-engine.js';
import { SmartAutocomplete } from '../search/smart-autocomplete.js';
import { SemanticAnalyzer } from '../search/semantic-analyzer.js';

export async function initializeSearchOrchestrator({ debug = false } = {}) {
    try {
        // ✅ CRITICAL: Register component status with weight (percentage)
        if(window.ComponentLoadingStatus){
            window.ComponentLoadingStatus.register('searchOrchestrator', 'loading', 'Initializing search engine...', 3);
        }
        

        // Initialize modules if they expose init(); most are auto-initialized on import
        if (typeof AdvancedSearchEngine !== 'undefined' && AdvancedSearchEngine && typeof AdvancedSearchEngine.init === 'function') {
            AdvancedSearchEngine.init();
        }

        if (typeof SmartAutocomplete !== 'undefined' && SmartAutocomplete && typeof SmartAutocomplete.init === 'function') {
            SmartAutocomplete.init();
        }

        if (typeof SemanticAnalyzer !== 'undefined' && SemanticAnalyzer && typeof SemanticAnalyzer.init === 'function') {
            SemanticAnalyzer.init();
        }

        // Make modules globally available
        window.AdvancedSearchEngine = AdvancedSearchEngine;
        window.SmartAutocomplete = SmartAutocomplete;
        window.SemanticAnalyzer = SemanticAnalyzer;

        // Create global wrapper functions
        window.performAdvancedSearch = (query, options) => {
            if (debug) console.log('[search-orchestrator] performAdvancedSearch called:', query, options);
            return AdvancedSearchEngine && AdvancedSearchEngine.search
                ? AdvancedSearchEngine.search(query, options)
                : null;
        };

        window.getSmartSuggestions = (input, context) => {
            if (debug) console.log('[search-orchestrator] getSmartSuggestions called:', input, context);
            return SmartAutocomplete && SmartAutocomplete.getSuggestions
                ? SmartAutocomplete.getSuggestions(input, context)
                : [];
        };

        window.analyzeQuerySemantics = (query) => {
            if (debug) console.log('[search-orchestrator] analyzeQuerySemantics called:', query);
            if (SemanticAnalyzer && typeof SemanticAnalyzer.analyzeQuery === 'function') {
                return SemanticAnalyzer.analyzeQuery(query);
            } else if (SemanticAnalyzer && typeof SemanticAnalyzer.analyze === 'function') {
                return SemanticAnalyzer.analyze(query);
            }
            // Basic fallback
            return {
                original: query,
                tokens: String(query || '').split(' '),
                concepts: [],
                intent: 'search'
            };
        };

        if (false) {
            console.log('[search-orchestrator] ✅ Global functions created');
            console.log('[search-orchestrator] Modules status:', {
                advancedSearchEngine: !!window.AdvancedSearchEngine,
                smartAutocomplete: !!window.SmartAutocomplete,
                semanticAnalyzer: !!window.SemanticAnalyzer
            });
        }

        // Ensure global availability after a tick (timing issue fix)
        setTimeout(() => {
            window.AdvancedSearchEngine = AdvancedSearchEngine;
            window.SmartAutocomplete = SmartAutocomplete;
            window.SemanticAnalyzer = SemanticAnalyzer;
        }, 100);

        // ✅ CRITICAL: Update component status to 'ready'
        if(window.ComponentLoadingStatus){
            let caption = 'Search engine initialized';
            if (window.TranslationRegistry && typeof window.TranslationRegistry.translate === 'function') {
              const lang = window.LanguageManager ? window.LanguageManager.getCurrentLanguage() : (window.LANG || 'EN');
              caption = window.TranslationRegistry.translate('loader_stage_search_engine_initialized', lang);
            }
            window.ComponentLoadingStatus.update('searchOrchestrator', 'ready', caption);
        }

        return true;
    } catch (error) {
        if (debug) console.error('[search-orchestrator] ❌ Initialization failed:', error);
        
        // ✅ CRITICAL: Update component status to 'error' on failure
        if(window.ComponentLoadingStatus){
            let caption = 'Search engine initialization failed';
            if (window.TranslationRegistry && typeof window.TranslationRegistry.translate === 'function') {
              const lang = window.LanguageManager ? window.LanguageManager.getCurrentLanguage() : (window.LANG || 'EN');
              caption = window.TranslationRegistry.translate('loader_stage_search_engine_init_failed', lang);
            }
            window.ComponentLoadingStatus.update('searchOrchestrator', 'error', caption);
        }
        
        return false;
    }
}


